import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Box, X, Loader2, FileDown, ExternalLink, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_3D = ['glb', 'gltf', 'obj', 'fbx'];
const ALLOWED_SOURCE = ['skp', 'dwg', 'dxf', '3ds', 'max', 'blend', 'stl', 'step', 'stp', 'iges', 'igs'];

const Model3DUploader = ({
  modelUrl,
  modelFormat,
  sourceFileUrl,
  sourceFileFormat,
  onChange,
  onSourceChange,
  dimensionsDisplay,
  materialsDisplay,
  onFieldChange,
}) => {
  const [uploading3D, setUploading3D] = useState(false);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const handleUpload3D = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_3D.includes(ext)) {
      toast.error(`Formato inválido. Use: ${ALLOWED_3D.join(', ').toUpperCase()}`);
      e.target.value = '';
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo grande demais (máx. 50MB)');
      e.target.value = '';
      return;
    }
    setUploading3D(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/upload/model`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url, res.data.format);
      toast.success(`Modelo 3D enviado (${(res.data.size / 1024 / 1024).toFixed(1)}MB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha no upload do modelo');
    } finally {
      setUploading3D(false);
      e.target.value = '';
    }
  };

  const handleUploadSource = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_SOURCE.includes(ext)) {
      toast.error(`Formato inválido. Use: ${ALLOWED_SOURCE.join(', ').toUpperCase()}`);
      e.target.value = '';
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Arquivo grande demais (máx. 100MB)');
      e.target.value = '';
      return;
    }
    setUploadingSource(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/upload/source-file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSourceChange(res.data.url, res.data.format);
      toast.success(`Arquivo ${ext.toUpperCase()} enviado (${(res.data.size / 1024 / 1024).toFixed(1)}MB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha no upload do arquivo');
    } finally {
      setUploadingSource(false);
      e.target.value = '';
    }
  };

  return (
    <div className="border border-border p-6 space-y-6 bg-neutral-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Box className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <h4 className="font-accent text-xs uppercase tracking-widest">Catálogo 3D</h4>
        </div>
        <button
          type="button"
          onClick={() => setShowTutorial((s) => !s)}
          data-testid="toggle-3d-tutorial"
          className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary"
        >
          <HelpCircle className="w-3 h-3" strokeWidth={1.5} />
          {showTutorial ? 'Ocultar' : 'Como exportar do SketchUp'}
        </button>
      </div>

      {showTutorial && (
        <div
          data-testid="3d-tutorial-box"
          className="bg-white border border-border p-4 text-xs text-muted-foreground space-y-2"
        >
          <p className="font-medium text-neutral-900">Como exportar do SketchUp para GLB (passo a passo):</p>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Abra o modelo no SketchUp Pro.</li>
            <li>Vá em <strong>Extension Warehouse</strong> e instale a extensão gratuita <strong>“glTF Export”</strong> (ou use o SketchUp 2024+ com Export nativo).</li>
            <li>Menu <strong>File → Export → 3D Model</strong> e escolha <strong>.glb</strong>.</li>
            <li>Marque a opção <em>“Embed textures”</em> para incluir as texturas dentro do arquivo.</li>
            <li>Anexe o <strong>.glb</strong> no campo &ldquo;Enviar modelo 3D&rdquo; abaixo e o <strong>.skp</strong> original em &ldquo;Enviar arquivo fonte&rdquo;.</li>
          </ol>
          <a
            href="https://extensions.sketchup.com/extension/f5aa9385-1jkl-4bda-b9d8-30f47eec0731/gl-tf-exporter"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
            Baixar extensão glTF Export
          </a>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Anexe um modelo 3D leve (GLB/GLTF/OBJ/FBX) para o visualizador do site. Opcionalmente, envie também o arquivo-fonte
        (ex: <strong>.skp</strong>) — clientes arquitetos poderão baixá-lo direto na página do produto.
      </p>

      {/* Upload Modelo 3D (visualização) */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          1. Modelo 3D — Visualizador (obrigatório para girar 360°)
        </p>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 flex items-center justify-center bg-white border border-border shrink-0">
            {modelUrl ? (
              <Box className="w-7 h-7 text-primary" strokeWidth={1.5} />
            ) : (
              <span className="text-[10px] text-muted-foreground">Nenhum</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {modelUrl ? (
              <>
                <p className="text-sm truncate">Modelo anexado</p>
                <p className="text-xs text-muted-foreground truncate">
                  Formato: <strong className="uppercase">{modelFormat || '—'}</strong>
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum modelo anexado</p>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <label className="flex items-center justify-center gap-2 border border-dashed border-border hover:border-primary bg-white px-4 py-2 cursor-pointer text-xs uppercase tracking-widest">
              <input
                type="file"
                accept=".glb,.gltf,.obj,.fbx,model/gltf-binary,model/gltf+json,application/octet-stream"
                className="hidden"
                onChange={handleUpload3D}
                disabled={uploading3D}
                data-testid="model-3d-upload"
              />
              {uploading3D ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading3D ? 'Enviando...' : 'Enviar modelo'}
            </label>
            {modelUrl && (
              <button
                type="button"
                onClick={() => onChange('', '')}
                className="text-xs text-muted-foreground hover:text-destructive flex items-center justify-center gap-1"
              >
                <X className="w-3 h-3" /> Remover
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Upload Arquivo Fonte (SketchUp) */}
      <div className="pt-4 border-t border-border">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          2. Arquivo Fonte SketchUp / CAD (opcional — apenas para download)
        </p>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 flex items-center justify-center bg-white border border-border shrink-0">
            {sourceFileUrl ? (
              <FileDown className="w-7 h-7 text-primary" strokeWidth={1.5} />
            ) : (
              <span className="text-[10px] text-muted-foreground">Nenhum</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {sourceFileUrl ? (
              <>
                <p className="text-sm truncate">Arquivo fonte anexado</p>
                <p className="text-xs text-muted-foreground truncate">
                  Formato: <strong className="uppercase">.{sourceFileFormat || '—'}</strong>
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum arquivo anexado</p>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <label className="flex items-center justify-center gap-2 border border-dashed border-border hover:border-primary bg-white px-4 py-2 cursor-pointer text-xs uppercase tracking-widest">
              <input
                type="file"
                accept=".skp,.dwg,.dxf,.3ds,.max,.blend,.stl,.step,.stp,.iges,.igs"
                className="hidden"
                onChange={handleUploadSource}
                disabled={uploadingSource}
                data-testid="source-file-upload"
              />
              {uploadingSource ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingSource ? 'Enviando...' : 'Enviar arquivo'}
            </label>
            {sourceFileUrl && (
              <button
                type="button"
                onClick={() => onSourceChange('', '')}
                className="text-xs text-muted-foreground hover:text-destructive flex items-center justify-center gap-1"
              >
                <X className="w-3 h-3" /> Remover
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
            Medidas (opcional)
          </label>
          <input
            type="text"
            data-testid="dimensions-display-input"
            value={dimensionsDisplay || ''}
            onChange={(e) => onFieldChange('dimensions_display', e.target.value)}
            placeholder="Ex: 180cm × 90cm × 85cm"
            className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
            Materiais (opcional)
          </label>
          <input
            type="text"
            data-testid="materials-display-input"
            value={materialsDisplay || ''}
            onChange={(e) => onFieldChange('materials_display', e.target.value)}
            placeholder="Ex: Madeira maciça, veludo, latão"
            className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-2 text-sm outline-none"
          />
        </div>
      </div>
    </div>
  );
};

export default Model3DUploader;
